import React, { useState, useEffect } from 'react';
import {
  FaCrown,
  FaCalendarAlt,
  FaTimes,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaArrowRight,
  FaStar,
  FaRocket,
  FaShieldAlt,
  FaCreditCard,
  FaInfoCircle,
  FaArrowUp,
  FaHistory,
  FaUserTag
} from 'react-icons/fa';
import axios from 'axios';
import './UserSubscriptionProfile.css';
import { setLoadingFalse, setLoadingTrue } from '../../../../Config/Actions/loadingActions';
import { useDispatch } from 'react-redux';
import { apiCallAmbassadorAssignByDirectAdmin, apiCallCancelDirectSubscription, apiCallFreePlanUpgraded, apiCallGetUserActiveSubscriptionPlan } from '../../../../Core/Apicall';
import { toast } from 'react-toastify';

const UserSubscriptionProfile = ({ userId }) => {
  const dispatch = useDispatch();
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showUpgradeInfoModal, setShowUpgradeInfoModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);

  const fetchUserSubscription = async () => {
    try {
      setLoading(true);
      const response = await apiCallGetUserActiveSubscriptionPlan({userId})
      if (response.success) {
        const data = response.data;
        const subscription = data.activeSubscription;
        const user = data.user;
        const allPlans = data.plans || [];
        let planType = 'FREE';
        let planName = 'Free Forever';
        let planPrice = '$0';
        let status = 'Inactive';
        let startDate = null;
        let endDate = null;
        let remainingDays = 0;
        let totalDays = 0;
        let isActive = false;
        let autoRenew = false;
        let features = [];
        let isUpgraded = false;
        let previousPlanTitle = '';
        let previousPlanDays = 0;
        let extendedDays = 0;
        let upgradeInfo = null;
        let planIcon = null;
        let activePlanId = null;
        let matchedPlan = null;
        if (subscription) {
          matchedPlan = allPlans.find(p => p._id === subscription.planId);
          activePlanId = subscription.planId;
        } else {
          const defaultFreePlan = allPlans.find(p => p.type === 'FREE' && p.isDefaultActive === true);
          if (defaultFreePlan) {
            matchedPlan = defaultFreePlan;
            activePlanId = defaultFreePlan._id;
          }
        }
        if (matchedPlan) {
          planType = matchedPlan.type;
          planName = matchedPlan.title;
          planPrice = matchedPlan.pricing?.displayPrice || '$0';
          features = matchedPlan.description?.split('\n').filter(f => f.trim()) || [];
          planIcon = matchedPlan.icon || null;

          // Check upgraded status
          isUpgraded = matchedPlan.isUpgraded || false;
          previousPlanTitle = matchedPlan.previousPlanTitle || '';
          previousPlanDays = matchedPlan.previousPlanDays || 0;
          extendedDays = matchedPlan.extendedDays || 0;

          // Calculate days (Only if subscription has expiry)
          if (subscription && subscription.expiryDate) {
            const expiry = new Date(subscription.expiryDate);
            const now = new Date();
            const diffTime = expiry - now;
            remainingDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

            const durationMap = {
              'MONTHLY': 30,
              'QUARTERLY': 90,
              'HALF_YEARLY': 182,
              'YEARLY': 365
            };
            totalDays = durationMap[matchedPlan.ambassadorDuration] || 30;

            if (isUpgraded && matchedPlan.currentPlanDays) {
              totalDays = matchedPlan.currentPlanDays;
            }

            endDate = subscription.expiryDate;
            const start = new Date(expiry);
            start.setDate(start.getDate() - totalDays);
            startDate = start.toISOString();
          }

          if (subscription) {
            status = subscription.status === 'ACTIVE' ? 'Active' : 'Inactive';
            isActive = subscription.status === 'ACTIVE';
            autoRenew = subscription.isAutoRenew || false;
          } else {
            status = 'Active';
            isActive = true;
          }

          if (isUpgraded) {
            upgradeInfo = {
              previousPlan: previousPlanTitle,
              previousDays: previousPlanDays,
              extendedDays: extendedDays,
              upgradedAt: subscription?.meta?.upgradedAt || null,
              isUpgrade: subscription?.meta?.isUpgrade || false
            };
          }
        }

        setUserSubscription({
          userId: user.userId || user._id,
          userName: user.userName,
          userEmail: user.userEmail,
          userType: user.userType,
          country: user.country || 'United States',
          subscription: {
            plan: planName,
            planType: planType,
            status: status,
            startDate: startDate,
            endDate: endDate,
            price: planPrice,
            currency: 'USD',
            autoRenew: autoRenew,
            totalDays: totalDays,
            remainingDays: remainingDays,
            productId: subscription?.productId || '',
            features: features,
            isActive: isActive,
            isUpgraded: isUpgraded,
            upgradeInfo: upgradeInfo,
            icon: planIcon
          }
        });

        // 5. Set Active Plan ID
        if (activePlanId) {
          setActivePlanId(activePlanId);
        }

        // 6. Format Available Plans (FREE & AMBASSADOR only)
        const formattedPlans = allPlans
          .filter(plan => plan.type === 'FREE' || plan.type === 'AMBASSADOR')
          .map(plan => {
            const isCurrentPlan = plan._id === activePlanId;
            const hasActiveSubscription = isCurrentPlan && isActive;

            return {
              id: plan._id,
              title: plan.title,
              type: plan.type,
              userType: plan.userType,
              userTypeDisplay: plan.userType,
              price: plan.pricing?.displayPrice || '$0',
              interval: plan.pricing?.interval?.toLowerCase() || 'monthly',
              features: plan.description?.split('\n').filter(f => f.trim()) || [],
              isCurrentPlan: isCurrentPlan,
              hasActiveSubscription: hasActiveSubscription,
              tag: plan.tag || (isCurrentPlan ? 'Current' : ''),
              isAccessible: true,
              accessHint: plan.accessHint || (isCurrentPlan ? '✓ Current Plan' : ''),
              isDefaultActive: plan.isDefaultActive || false,
              isFree: plan.isFree || false,
              isAmbassadorPlan: plan.isAmbassadorPlan || false,
              requiresApproval: plan.requiresApproval || false,
              availableFromDate: plan.availableFromDate,
              daysUntilAvailable: plan.daysUntilAvailable || 0,
              isUpgraded: plan.isUpgraded || false,
              achievementRequirements: plan.achievementRequirements || null,
              ambassadorDuration: plan.ambassadorDuration || null,
              icon: plan.icon || null
            };
          });

        // Sort & Set Available Plans
        const sortedFormattedPlans = formattedPlans.sort((a, b) => {
          const order = { 'FREE': 0, 'AMBASSADOR': 1 };
          return (order[a.type] || 2) - (order[b.type] || 2);
        });

        setAvailablePlans(sortedFormattedPlans);
        setSubscriptionPlans(allPlans);

      } else {
        setError(response.data.message || 'Failed to fetch subscription');
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.response?.data?.message || 'Error fetching subscription data');
    } finally {
      setLoading(false);
    }
  };

  // ✅ MERGED: Single useEffect to fetch everything
  useEffect(() => {
    if (userId) {
      fetchUserSubscription();
    }
  }, [userId]);

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'Active': { color: '#10b981', bg: 'rgba(16, 185, 129, 0.2)', icon: <FaCheckCircle />, label: 'Active' },
      'PENDING': { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.2)', icon: <FaClock />, label: 'Pending' },
      'Expired': { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.2)', icon: <FaExclamationCircle />, label: 'Expired' },
      'Cancelled': { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.2)', icon: <FaTimes />, label: 'Cancelled' },
      'Inactive': { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.2)', icon: <FaTimes />, label: 'Inactive' }
    };
    return statusMap[status] || statusMap['Inactive'];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch {
      return 'N/A';
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (!userSubscription?.subscription) return 0;
    const { totalDays, remainingDays } = userSubscription.subscription;
    if (!totalDays || totalDays === 0) return 0;
    return ((remainingDays / totalDays) * 100).toFixed(0);
  };

  const handleCancelSubscription = async () => {
    dispatch(setLoadingTrue());
    try {
      await apiCallCancelDirectSubscription({
        userId: userId,
        planId: activePlanId,
      });
      setShowCancelModal(false);
      toast.success('Ambassador plan cancelled successfully');
      await fetchUserSubscription();
    } catch (e) {
      toast.error('Error cancelling subscription');
    } finally {
      dispatch(setLoadingFalse());
    }
  };

  // Handle change plan
  const handleChangePlan = () => {
    setShowChangePlanModal(true);
    setSelectedPlan(null);
  };

  const selectPlan = (planId) => {
    const plan = availablePlans.find(p => p.id === planId);

    // 1. Agar plan hi nahi mila toh return
    if (!plan) return;

    // 2. Agar plan already current hai
    if (plan.isCurrentPlan) {
      toast.info("This plan is already active and cannot be selected.");
      return;
    }

    // 3. Agar user ke paas already Active Ambassador plan hai, toh free plans select nahi hone chahiye
    if (userSubscription?.subscription?.planType === 'AMBASSADOR' && userSubscription?.subscription?.isActive) {
      if (plan.type === 'FREE') {
        toast.info("You have an active Ambassador plan. FREE plans are not available.");
        return;
      }
    }

    // 4. Agar sab sahi hai toh select karo
    setSelectedPlan(plan);
  };

  const confirmPlanChange = async () => {
    console.log("selectedPlan", selectedPlan);

    if (!selectedPlan) return;
    if (selectedPlan.type === "FREE") {
      const currentUserType = userSubscription.userType;
      const targetUserType = selectedPlan.userType;
      if (currentUserType === "ArtLover" && targetUserType === "Artist") {
        try {
          dispatch(setLoadingTrue());
          const res = await apiCallFreePlanUpgraded({
            userId: userId,
            userType: selectedPlan.userType
          });
          setShowChangePlanModal(false);
          dispatch(setLoadingFalse());
          await fetchUserSubscription();
          toast.success('Plan changed to Free successfully');
        } catch (error) {
          dispatch(setLoadingFalse());
          toast.error('Error changing plan');
        }
      }
      else {
        toast.error(`You cannot switch from ${currentUserType} to ${targetUserType}. Only ArtLover can upgrade to Artist.`);
        setShowChangePlanModal(false);
      }
    }
    else if (selectedPlan.type === "AMBASSADOR") {
      try {
        dispatch(setLoadingTrue());
        const res = await apiCallAmbassadorAssignByDirectAdmin({
          userId: userId,
          planId: selectedPlan.id
        });
        setShowChangePlanModal(false);
        dispatch(setLoadingFalse());
        await fetchUserSubscription();
        toast.success('Plan changed to Ambassador successfully');
      } catch (error) {
        dispatch(setLoadingFalse());
        toast.error('Error changing plan');
      }
    }
  };

  const getPlanTypeClass = (type) => {
    const classes = {
      'FREE': 'plan-type-free',
      'PRO': 'plan-type-pro',
      'AMBASSADOR': 'plan-type-ambassador'
    };
    return classes[type] || 'plan-type-pro';
  };

  const getUserTypeClass = (userType) => {
    const classes = {
      'ArtLover': 'user-type-artlover',
      'Artist': 'user-type-artist',
      'ArtistPro': 'user-type-artistpro',
      'ArtRunner': 'user-type-artrunner'
    };
    return classes[userType] || 'user-type-default';
  };

  const renderActionButtons = () => {
    const { planType, isActive, isUpgraded } = userSubscription.subscription;

    if (planType === 'AMBASSADOR' && isActive) {
      return (
        <div className="action-buttons ambassador-actions">
          {isUpgraded && (
            <button
              className="btn-action btn-upgrade-info-action"
              onClick={() => setShowUpgradeInfoModal(true)}
            >
              <FaHistory /> Upgrade Info
            </button>
          )}
          <button
            className="btn-action btn-change-action"
            onClick={handleChangePlan}
          >
            <FaArrowRight /> Change Plan
          </button>
          <button
            className="btn-action btn-cancel-action"
            onClick={() => setShowCancelModal(true)}
          >
            <FaTimes /> Cancel Ambassador
          </button>
        </div>
      );
    }

    if (planType === 'FREE') {
      return (
        <div className="action-buttons free-actions">
          <button
            className="btn-action btn-change-action"
            onClick={handleChangePlan}
          >
            <FaArrowRight /> Change Plan
          </button>
        </div>
      );
    }

    if (planType === 'PRO' && isActive) {
      return null;
    }

    return null;
  };

  const renderUpgradeInfo = () => {
    const { upgradeInfo, isUpgraded } = userSubscription.subscription;
    if (!isUpgraded || !upgradeInfo) return null;

    return (
      <div className="upgrade-info-banner">
        <div className="upgrade-header">
          <FaArrowUp className="upgrade-icon" />
          <span className="upgrade-title">Plan Upgraded</span>
        </div>
        <div className="upgrade-details">
          <div className="upgrade-item">
            <span className="upgrade-label">Previous Plan:</span>
            <span className="upgrade-value">{upgradeInfo.previousPlan || 'N/A'}</span>
          </div>
          <div className="upgrade-item">
            <span className="upgrade-label">Previous Days:</span>
            <span className="upgrade-value">{upgradeInfo.previousDays} days</span>
          </div>
          <div className="upgrade-item">
            <span className="upgrade-label">Extended By:</span>
            <span className="upgrade-value highlight">{upgradeInfo.extendedDays} days</span>
          </div>
          {upgradeInfo.upgradedAt && (
            <div className="upgrade-item">
              <span className="upgrade-label">Upgraded On:</span>
              <span className="upgrade-value">{formatDate(upgradeInfo.upgradedAt)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAchievementRequirements = (plan) => {
    if (!plan.achievementRequirements) return null;

    const reqs = plan.achievementRequirements;
    return (
      <div className="achievement-requirements">
        <div className="req-item">
          <span className="req-icon">✅</span>
          <span className="req-text">{reqs.check_ins?.title}: {reqs.check_ins?.requiredCount} check-ins</span>
        </div>
        <div className="req-item">
          <span className="req-icon">🎨</span>
          <span className="req-text">{reqs.media_uploads?.title}: {reqs.media_uploads?.requiredCount} uploads</span>
        </div>
        <div className="req-item">
          <span className="req-icon">👥</span>
          <span className="req-text">{reqs.referrals?.title}: {reqs.referrals?.requiredCount} referrals</span>
        </div>
      </div>
    );
  };

  const renderPlanPrice = (plan) => {
    if (plan.type === 'FREE') {
      return <span className="plan-option-price free">Free</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="user-subscription-profile">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading subscription data...</p>
        </div>
      </div>
    );
  }

  if (error || !userSubscription) {
    return (
      <div className="user-subscription-profile">
        <div className="error-container">
          <FaExclamationCircle className="error-icon" />
          <p>{error || 'No subscription data available'}</p>
        </div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(userSubscription.subscription.status);
  const isActive = userSubscription.subscription.status === 'Active';

  return (
    <div className="user-subscription-profile">
      <div className="profile-card">
        {/* Subscription Section */}
        <div className="subscription-section">
          <div className="subscription-header">
            <div className="subscription-title">
              <FaCrown className="crown-icon" />
              <h3>Current Subscription</h3>
            </div>
            {userSubscription.subscription.autoRenew && (
              <span className="auto-renew-badge">Auto-Renew ON</span>
            )}
          </div>

          <div className="subscription-details">
            {/* Plan Card */}
            <div className="subscription-plan-card">
              <div className="plan-main-info">
                <div className="plan-icon-wrapper">
                  {/* ✅ API se icon aa raha hai to image dikhao, warna kuch mat dikhao */}
                  {userSubscription.subscription.icon ? (
                    <img
                      src={userSubscription.subscription.icon}
                      alt="Plan icon"
                      className="plan-icon-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : null}
                </div>

                <div className="plan-info-group">
                  <span className="plan-name">{userSubscription.subscription.plan}</span>
                  <span className={`plan-type-badge ${getPlanTypeClass(userSubscription.subscription.planType)}`}>
                    {userSubscription.subscription.planType}
                  </span>

                  {userSubscription.subscription.planType === 'PRO' && (
                    <span className="plan-price-badge">{userSubscription.subscription.price}/month</span>
                  )}

                  {userSubscription.subscription.planType === 'FREE' && (
                    <>
                      <span className="plan-price-badge free">Free Forever</span>
                      {/* ✅ UserType Badge FREE plan ke liye */}
                      <span className={`user-type-badge ${getUserTypeClass(userSubscription.userType)}`}>
                        <FaUserTag className="user-type-icon" />
                        {userSubscription.userType}
                      </span>
                    </>
                  )}

                  {userSubscription.subscription.planType === 'AMBASSADOR' && (
                    <>
                      <span className="plan-price-badge ambassador">Admin Managed</span>
                      <span className={`user-type-badge ${getUserTypeClass(userSubscription.userType)}`}>
                        <FaUserTag className="user-type-icon" />
                        {userSubscription.userType}
                      </span>
                    </>
                  )}

                  {userSubscription.subscription.isUpgraded && (
                    <span className="upgraded-badge">⬆ Upgraded</span>
                  )}
                </div>
              </div>
              <div className="plan-status-wrapper">
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: statusBadge.bg,
                    color: statusBadge.color
                  }}
                >
                  {statusBadge.icon}
                  {statusBadge.label}
                </span>
              </div>
            </div>

            {/* Upgrade Info Banner for AMBASSADOR - Only when upgraded */}
            {userSubscription.subscription.planType === 'AMBASSADOR' &&
              userSubscription.subscription.isUpgraded &&
              renderUpgradeInfo()}

            {/* Subscription Meta */}
            <div className="subscription-meta">
              {userSubscription.subscription.startDate && (
                <div className="meta-item">
                  <FaCalendarAlt className="meta-icon" />
                  <div>
                    <label>Started</label>
                    <span>{formatDate(userSubscription.subscription.startDate)}</span>
                  </div>
                </div>
              )}
              {userSubscription.subscription.endDate && (
                <div className="meta-item">
                  <FaCalendarAlt className="meta-icon" />
                  <div>
                    <label>Ends</label>
                    <span>{formatDate(userSubscription.subscription.endDate)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Days Remaining - Only for PRO and AMBASSADOR with Active status */}
            {(userSubscription.subscription.planType === 'PRO' ||
              userSubscription.subscription.planType === 'AMBASSADOR') &&
              isActive &&
              userSubscription.subscription.totalDays > 0 && (
                <div className="days-remaining">
                  <div className="days-header">
                    <div className="days-badge">
                      <span className="days-number">{userSubscription.subscription.remainingDays}</span>
                      <span className="days-label">days remaining</span>
                    </div>
                    <span className="days-total">of {userSubscription.subscription.totalDays} days</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${getProgressPercentage()}%`
                      }}
                    ></div>
                  </div>
                  {userSubscription.subscription.isUpgraded && (
                    <div className="extended-info">
                      <span className="extended-label">✨ Extended from {userSubscription.subscription.upgradeInfo?.previousDays || 0} days</span>
                    </div>
                  )}
                </div>
              )}

            {/* FREE Plan specific info */}
            {userSubscription.subscription.planType === 'FREE' && (
              <div className="free-plan-info">
                <FaShieldAlt className="free-icon" />
                <div>
                  <p className="free-title">Free Forever Plan</p>
                  <p className="free-subtitle">No time limit</p>
                </div>
              </div>
            )}

            {/* AMBASSADOR Plan specific info */}
            {userSubscription.subscription.planType === 'AMBASSADOR' && (
              <div className="ambassador-info">
                <FaRocket className="ambassador-icon" />
                <div>
                  <p className="ambassador-title">Ambassador Program</p>
                  <p className="ambassador-subtitle">
                    {userSubscription.subscription.isUpgraded
                      ? `✨ Upgraded • Extended by ${userSubscription.subscription.upgradeInfo?.extendedDays || 0} days`
                      : 'Managed by admin • Limited period'}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {renderActionButtons()}
          </div>
        </div>
      </div>

      {/* Cancel Modal - UPDATED with No/Yes buttons and NO reason field */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Subscription</h3>
              <button className="close-btn" onClick={() => setShowCancelModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="warning-box">
                <p><strong>⚠️ Are you sure you want to cancel your {userSubscription.subscription.planType} plan?</strong></p>
                <ul>
                  <li>Your access will continue until <strong>{formatDate(userSubscription.subscription.endDate)}</strong></li>
                  <li>You will lose access immediately after the expiry date</li>
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCancelModal(false)}>
                No
              </button>
              <button
                className="btn-danger"
                onClick={handleCancelSubscription}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Info Modal */}
      {showUpgradeInfoModal && userSubscription.subscription.upgradeInfo && (
        <div className="modal-overlay" onClick={() => setShowUpgradeInfoModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upgrade Information</h3>
              <button className="close-btn" onClick={() => setShowUpgradeInfoModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="upgrade-info-modal">
                <div className="upgrade-icon-large">
                  <FaArrowUp />
                </div>
                <h4>Plan Upgrade Details</h4>
                <div className="upgrade-details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Previous Plan</span>
                    <span className="detail-value">{userSubscription.subscription.upgradeInfo.previousPlan || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Previous Days</span>
                    <span className="detail-value">{userSubscription.subscription.upgradeInfo.previousDays} days</span>
                  </div>
                  <div className="detail-item highlight-item">
                    <span className="detail-label">Extended By</span>
                    <span className="detail-value highlight">{userSubscription.subscription.upgradeInfo.extendedDays} days</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">New Total</span>
                    <span className="detail-value">{userSubscription.subscription.totalDays} days</span>
                  </div>
                  {userSubscription.subscription.upgradeInfo.upgradedAt && (
                    <div className="detail-item">
                      <span className="detail-label">Upgraded On</span>
                      <span className="detail-value">{formatDate(userSubscription.subscription.upgradeInfo.upgradedAt)}</span>
                    </div>
                  )}
                </div>
                <div className="upgrade-summary">
                  <p>✨ Your plan was successfully upgraded from <strong>{userSubscription.subscription.upgradeInfo.previousDays}</strong> days to <strong>{userSubscription.subscription.totalDays}</strong> days, giving you <strong>{userSubscription.subscription.upgradeInfo.extendedDays}</strong> additional days!</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowUpgradeInfoModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal - Shows FREE and AMBASSADOR plans with icons */}
      {showChangePlanModal && (
        <div className="modal-overlay" onClick={() => setShowChangePlanModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Available Plans</h3>
              <button className="close-btn" onClick={() => setShowChangePlanModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="current-plan-summary">
                <div className="summary-current">
                  <span className="summary-label">Current Plan:</span>
                  <span className="summary-value">{userSubscription.subscription.plan}</span>
                  <span className={`plan-type-badge ${getPlanTypeClass(userSubscription.subscription.planType)}`}>
                    {userSubscription.subscription.planType}
                  </span>
                  <span className="active-status-badge">✅ Active</span>
                </div>
                {userSubscription.subscription.planType !== 'FREE' && userSubscription.subscription.remainingDays > 0 && (
                  <div className="summary-remaining">
                    <span className="summary-label">Remaining:</span>
                    <span className="summary-value">{userSubscription.subscription.remainingDays} days</span>
                    {userSubscription.subscription.endDate && (
                      <span className="summary-sub">until {formatDate(userSubscription.subscription.endDate)}</span>
                    )}
                  </div>
                )}
              </div>

              <div className="available-plans">
                <h4>Available Plans</h4>
                {availablePlans.map((plan) => {
                  const isActivePlan = plan.isCurrentPlan && plan.hasActiveSubscription;

                  // ✅ UPDATED: Agar Ambassador active hai aur plan FREE hai toh selectable = false
                  const isAmbassadorActiveAndPlanFree =
                    userSubscription?.subscription?.planType === 'AMBASSADOR' &&
                    userSubscription?.subscription?.isActive &&
                    plan.type === 'FREE';

                  // Agar current active plan hai, ya ambassador active me free plan hai, toh select nahi ho payega
                  const isSelectable = !plan.isCurrentPlan && !isAmbassadorActiveAndPlanFree;

                  return (
                    <div
                      key={plan.id}
                      className={`plan-option ${selectedPlan?.id === plan.id ? 'selected' : ''} ${isActivePlan ? 'active-plan' : ''}`}
                      // ✅ onClick mein "isSelectable" ka condition check hai
                      onClick={() => isSelectable && selectPlan(plan.id)}
                      style={{
                        opacity: !isSelectable ? 0.6 : 1, // Disabled plans thode transparent dikhenge
                        cursor: isSelectable ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <div className="plan-option-header">
                        <div className="plan-option-info">
                          {/* Show plan icon in available plans */}
                          <div className="plan-option-icon-wrapper">
                            {plan.icon ? (
                              <img
                                src={plan.icon}
                                alt={`${plan.type} icon`}
                                className="plan-option-icon-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              plan.type === 'AMBASSADOR' ? (
                                <FaRocket className="plan-option-icon-fallback" />
                              ) : plan.type === 'FREE' ? (
                                <FaShieldAlt className="plan-option-icon-fallback" />
                              ) : (
                                <FaStar className="plan-option-icon-fallback" />
                              )
                            )}
                          </div>
                          <strong>{plan.title}</strong>
                          <span className={`plan-type-badge ${getPlanTypeClass(plan.type)}`}>
                            {plan.type}
                          </span>
                          <span className={`user-type-badge ${getUserTypeClass(plan.userType)}`}>
                            <FaUserTag className="user-type-icon" />
                            {plan.userType}
                          </span>
                          {isActivePlan && (
                            <span className="active-badge">✅ Active</span>
                          )}
                          {plan.tag && !isActivePlan && (
                            <span className="plan-tag">{plan.tag}</span>
                          )}
                          {plan.type === 'FREE' && !isActivePlan && (
                            <span className="plan-free-badge">Free Forever</span>
                          )}
                          {plan.type === 'AMBASSADOR' && !isActivePlan && (
                            <span className="plan-ambassador-badge">🌟 Ambassador</span>
                          )}
                          {plan.daysUntilAvailable > 0 && !isActivePlan && (
                            <span className="plan-available-badge">
                              Available in {plan.daysUntilAvailable} days
                            </span>
                          )}
                        </div>
                        {renderPlanPrice(plan)}
                      </div>
                      {plan.accessHint && (
                        <p className="plan-access-hint">{plan.accessHint}</p>
                      )}
                      {plan.type === 'AMBASSADOR' && plan.achievementRequirements && (
                        <div className="plan-ambassador-requirements">
                          <div className="req-title">Requirements:</div>
                          {renderAchievementRequirements(plan)}
                        </div>
                      )}
                      {plan.features.length > 0 && (
                        <div className="plan-features-list">
                          {plan.features.slice(0, 3).map((feature, idx) => (
                            <span key={idx} className="feature-tag">• {feature.trim()}</span>
                          ))}
                          {plan.features.length > 3 && (
                            <span className="feature-more">+{plan.features.length - 3} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {availablePlans.length === 0 && (
                  <div className="no-plans-message">
                    <FaInfoCircle className="info-icon" />
                    <p>No plans available at the moment.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowChangePlanModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={confirmPlanChange}
                disabled={!selectedPlan || selectedPlan.isCurrentPlan}
              >
                {selectedPlan?.type === 'FREE' ? 'Switch to Free' :
                  selectedPlan?.type === 'AMBASSADOR' ? 'Apply for Ambassador' :
                    'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSubscriptionProfile;